import * as vp from 'vopay';
import { FunctionComponent, useEffect, useState } from 'react';
import { useHistory, Prompt } from 'react-router-dom';
import userPostCreateUser from '../../../hooks/useApi/usePostCreateUser';
import CreateUserForm from './CreateUserForm';
import CreateUserContext, { CreateUserCtx } from './CreateUserContext';
import CreateUserDialogue from './CreateUserDialogue';
import { CreateUserDialogueHandler, DialogueHandlerProps } from '../types';
import { usePendingData } from '../../../providers/PendingData.provider';
import { useTranslation } from 'react-i18next';

const { Box, Title, BackButton } = vp;

type FC = FunctionComponent;

const CreateUserContainer: FC = () => {
  const history = useHistory();
  const [CreateUserError, setCreateUserError] = useState('');
  const [showCreateUserErrorAlert, setShowCreateUserErrorAlert] = useState(false);
  const [showCreateUserSuccessAlert, setShowCreateUserSuccessAlert] = useState(false);
  const [shouldCreateUser, setShouldCreateUser] = useState(false);
  const [CreateUserResponse, createUser] = userPostCreateUser({});
  const [reviewing, setReviewing] = useState(false);
  const { initPendingData, updatePendingData, hasPendingData } = usePendingData();
  const { t } = useTranslation('translations', { keyPrefix: 'Users.Create' });

  const [defaultCreateUser, setDefaultCreateUser] = useState<vp.CreateUserRequest>({
    EmailAddress: '',
    SendEmail: true,
    Role: '',
  });

  const CreateUser: CreateUserCtx = {
    showCreateUserSuccessAlert,
    showCreateUserErrorAlert,
    CreateUserError,
  };

  useEffect(() => {
    initPendingData({ ...defaultCreateUser });
  }, []);
  useEffect(() => {
    updatePendingData({ ...defaultCreateUser });
  }, [defaultCreateUser]);

  const handlers = {
    handleDialogue: (handlerProps: DialogueHandlerProps<CreateUserDialogueHandler>) => {
      const { type, state, action = 'default' } = handlerProps;

      const dialogueHandlers: CreateUserDialogueHandler = {
        create: {
          success: {
            default: () => {
              setShowCreateUserSuccessAlert(false);
              history.push('/users');
            },
          },
          fail: {
            default: () => {
              setShowCreateUserErrorAlert(false);
              setCreateUserError('');
            },
          },
        },
      };
      const dialogueState = dialogueHandlers[type][state];
      const handler = dialogueState[action] || dialogueState.default;

      handler();
    },
    onChangeCreateUser: (values: vp.CreateUserRequest) => {
      setShouldCreateUser(true);

      createUser(values);
    },

    onReview: (values: vp.CreateUserRequest) => {
      setDefaultCreateUser(values);
      setReviewing(true);
    },
  };

  useEffect(() => {
    if (shouldCreateUser) {
      setShouldCreateUser(false);

      const { Success, ErrorMessage } = CreateUserResponse;

      if (Success === true) {
        setShowCreateUserSuccessAlert(true);
      } else {
        setCreateUserError(ErrorMessage);
        setShowCreateUserErrorAlert(true);
      }
    }
  }, [CreateUserResponse]);

  return (
    <CreateUserContext.Provider value={CreateUser}>
      <Box mt="sm" mb="sm" jc="fs" ai="center" gap="common">
        {reviewing ? (
          <>
            <BackButton
              variant="arrow"
              backButtonEnabled
              onClick={() => {
                setReviewing(false);
              }}
            />
            <Title title={t('Review.pageTitle')} />
          </>
        ) : (
          <>
            <BackButton variant="arrow" backButtonEnabled />
            <Title title={t('pageTitle')} />
          </>
        )}
      </Box>
      <CreateUserForm defaultValues={defaultCreateUser} handlers={handlers} reviewing={reviewing} />
      <Prompt when={hasPendingData} message={t('pendingPrompt')} />

      <CreateUserDialogue handleDialogue={handlers.handleDialogue} />
    </CreateUserContext.Provider>
  );
};

export default CreateUserContainer;
